import { Router, UrlTree } from "@angular/router";
import { inject } from "@angular/core";
import { ENV } from "src/providers/environment.provider";
import { Environment } from "src/types/environment";
import { LoginService } from "login";
import { StaticDataService } from "@services/static-data/static-data.service";
import { sortByProperties } from "@utilities/arrays/arrays";
import { UserService } from "@services/user/user.service";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Organisation } from "src/types/organisation";

const NO_ACCESS_URL = 'no-access';
let ACCESS_CHECKED = false;
let NO_ACCESS = false;

export class AuthGuard {
    public static async canLoadApp(): Promise<UrlTree | boolean> {
        ACCESS_CHECKED = true;

        const router = inject(Router);
        const userService = inject(UserService);
        const staticDataService = inject(StaticDataService);

        const validationResult = await AuthGuard.validateUser(router);
        if (validationResult != null) { return validationResult; }

        let selectedLegalEntity: Organisation | undefined;

        try {
            selectedLegalEntity = await userService.getSelectedLegalEntity();
        }
        catch (err) {
            if (err instanceof HttpErrorResponse && err.status == HttpStatusCode.Forbidden) { return AuthGuard.navigateToNoAccess(router); }

            throw err;
        }

        if (!selectedLegalEntity) {
            const legalEntities = await userService.getAvailableLegalEntities();
            
            if (legalEntities.length === 0) {
                console.error('The user does not have access to any legal entities');
                return AuthGuard.navigateToNoAccess(router);
            }

            const firstTenantOrg = sortByProperties(legalEntities, ['id'], true)[0];
            await userService.setSelectedLegalEntity(firstTenantOrg.id.toString());
        }

        await staticDataService.refreshStaticData();
        return true;
    }

    public static async canSelectOrganisation(): Promise<UrlTree | boolean> {
        const router = inject(Router);
        const validationResult = await AuthGuard.validateUser(router);
        if (validationResult != null) { return validationResult; }

        return true;
    }

    public static async noAccessGuard(): Promise<UrlTree | boolean> {
        const router = inject(Router);
        return ACCESS_CHECKED && NO_ACCESS
            ? true
            : router.createUrlTree([]);
    }

    private static async validateUser(router: Router): Promise<UrlTree | boolean | null> {
        const environment = inject<Environment>(ENV);
        const loginService: LoginService = inject(LoginService);
        if (!await loginService.getUserEmail()) {
            if (!environment.loginHost) { return router.createUrlTree(['login']); }

            location.href = `${environment.loginHost}/login?redirect=${location.href}`;
            return false;
        }
        return null;
    }

    private static navigateToNoAccess(router: Router): UrlTree | boolean {
        NO_ACCESS = true;
        // User does not have access to any orgs
        return router.url.endsWith(NO_ACCESS_URL) ? true : router.createUrlTree([NO_ACCESS_URL]);
    }
}